// 5-step guided setup that walks users to a printable emergency sheet.
// Opens from dashboard promo card or avatar dropdown "Emergency Sheet" link.
import { useState, useEffect } from 'react'
import { supabase } from '../../supabase'
import { emitTourEvent, TOUR_EVENT_TYPES } from '../../lib/appEvents'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', successBg: '#EBF5EF',
  danger: '#B03020', dangerBg: '#FDECEA',
  sheetBg: '#1A1008',
}
const geo = 'Georgia, serif'
const cal = 'Calibri, sans-serif'

const RELATIONSHIPS = ['self', 'owner', 'spouse', 'partner', 'child', 'parent', 'sibling', 'family', 'roommate', 'caretaker', 'caregiver', 'other']
const PET_TYPES = ['Dog', 'Cat', 'Bird', 'Fish', 'Rabbit', 'Reptile', 'Other']

const PRESET_CONTACTS = [
  'Family Contact',
  'Neighbor',
  'Doctor / Medical',
  'Vet',
  'Plumber',
  'Electrician',
]
// Always-included national emergency services. Auto-added on Step C so they
// appear on the printed sheet even if the user skips the address lookup.
const DEFAULT_LOCAL_SERVICES = [
  { name: '988 Suicide & Crisis Lifeline', phone: '988',        service_type: 'crisis',         is_default: true },
  { name: 'Poison Control',                phone: '8002221222', service_type: 'poison_control', is_default: true },
]
// Service type display: icon + readable label. Used in the preview and PDF.
const SERVICE_TYPE_DISPLAY = {
  police:           { icon: '🚔', label: 'Police' },
  fire:             { icon: '🚒', label: 'Fire' },
  sheriff:          { icon: '🏛️', label: 'Sheriff' },
  hospital:         { icon: '🏥', label: 'Hospital' },
  poison_control:   { icon: '☠️', label: 'Poison Control' },
  crisis:           { icon: '🧠', label: 'Crisis' },
  vet_emergency:    { icon: '🐾', label: 'Vet' },
  utility_electric: { icon: '⚡', label: 'Electric' },
  utility_gas:      { icon: '🔥', label: 'Gas' },
  utility_water:    { icon: '💧', label: 'Water' },
  utility:          { icon: '🔌', label: 'Utility' },
  pharmacy:         { icon: '💊', label: 'Pharmacy' },
  locksmith:        { icon: '🔑', label: 'Locksmith' },
  plumber:          { icon: '🔧', label: 'Plumber' },
  other:            { icon: '📞', label: 'Other' },
}
// Display formatter for US phone numbers. Short codes (3-4 digit) pass through.
function formatPhoneDisplay(raw) {
  if (!raw) return ''
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length <= 4) return digits // short codes like 988, 911, 311
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  return raw // unknown format, return as-is
}

function formatPhoneInput(raw) {
  const digits = String(raw).replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

function validateStepA(members) {
  for (const m of members) {
    if (m.is_pet || m.relationship === 'self') continue
    if (!m.first_name.trim() && !m.last_name.trim())
      return 'Please enter at least a first or last name for each person.'
    if (m.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m.email.trim()))
      return 'Please enter a valid email address, or leave it blank.'
    if (m.phone.trim()) {
      const digits = m.phone.replace(/\D/g, '')
      if (digits.length !== 10 && !(digits.length === 11 && digits.startsWith('1')))
        return 'Please enter a valid 10-digit US phone number, or leave it blank.'
    }
  }
  return null
}

// ── PREVIEW PANE ────────────────────────────────────────────────────────────

function SheetPreview({ members, contacts, localServices = [], shutoffs, homeAddress }) {
  const validMembers = members.filter(m =>
    m.is_pet ? m.name?.trim() : (m.first_name?.trim() || m.last_name?.trim() || m.name?.trim())
  )
  const validContacts = contacts.filter(c => c.name.trim())
  const validLocal = localServices.filter(s => s.name?.trim())
  const hasShutoffs = shutoffs.water || shutoffs.gas || shutoffs.electric || shutoffs.sewer
  const hasLockbox = shutoffs.lockbox_code || shutoffs.lockbox_location
  const hasAnything = validMembers.length || validContacts.length || validLocal.length || hasShutoffs || hasLockbox || shutoffs.notes

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 12,
      overflow: 'hidden', fontSize: 12, fontFamily: 'Arial, sans-serif',
      background: '#fff', minHeight: 300,
    }}>
      {/* Sheet header */}
      <div style={{ background: C.sheetBg, color: '#FDFAF6', padding: '12px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 3 }}>CASAVIO</div>
        <div style={{ fontSize: 10, color: '#9A7B68', marginTop: 2 }}>Emergency Information Sheet</div>
      </div>

      {/* Address bar */}
      <div style={{ background: C.amber, color: '#fff', padding: '7px 16px', fontSize: 12, fontWeight: 700 }}>
        {homeAddress || '— your address will appear here —'}
      </div>

      {/* Emergency box — tap-to-call on mobile */}
      <a href="tel:911" style={{ display: 'block', textDecoration: 'none' }}>
        <div style={{ background: '#DC2626', color: '#fff', margin: '12px 16px', borderRadius: 6, padding: '8px', textAlign: 'center', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          EMERGENCY — CALL 911
        </div>
      </a>

      {!hasAnything && (
        <div style={{ padding: '20px 16px', color: C.muted, textAlign: 'center', fontSize: 12, fontStyle: 'italic' }}>
          Fill in the fields to see your sheet take shape…
        </div>
      )}

      {validMembers.length > 0 && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid #F2EDE4`, paddingBottom: 4, marginBottom: 8 }}>Household Members</div>
          {validMembers.map((m, i) => (
            <div key={i} style={{ paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid #F2EDE4` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>
                    {m.is_pet ? m.name : (`${m.first_name || ''} ${m.last_name || ''}`.trim() || m.name)}
                    {m.is_pet ? ` — ${m.pet_type || 'Pet'}` : ''}
                  </div>
                  {!m.is_pet && <div style={{ fontSize: 10, color: C.muted }}>{m.relationship}</div>}
                </div>
              </div>
              {!m.is_pet && m.non_english_languages && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Speaks: {m.non_english_languages}</div>}
              {!m.is_pet && m.preferred_hospital && (
                <div style={{ background: '#FFF8E7', border: `1px solid #B8721F`, borderRadius: 4, padding: '4px 8px', marginTop: 4, fontSize: 10, color: '#B8721F' }}>
                  Hospital: {m.preferred_hospital}{m.preferred_hospital_phone ? ` · ${m.preferred_hospital_phone}` : ''}
                  {m.preferred_hospital_notes ? ` — ${m.preferred_hospital_notes}` : ''}
                </div>
              )}
              {!m.is_pet && !m.preferred_hospital && m.preferred_hospital_notes && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{m.preferred_hospital_notes}</div>}
            </div>
          ))}
        </div>
      )}

      {validLocal.length > 0 && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid #F2EDE4`, paddingBottom: 4, marginBottom: 8 }}>Emergency Services</div>
          {validLocal.map((s, i) => {
            const cfg = SERVICE_TYPE_DISPLAY[s.service_type] || SERVICE_TYPE_DISPLAY.other
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4` }}>
                <div style={{ fontSize: 12, minWidth: 0, flex: 1 }}>
                  <span style={{ marginRight: 4 }}>{cfg.icon}</span>
                  <span style={{ color: C.muted, fontWeight: 600 }}>{cfg.label} · </span>
                  <span style={{ fontWeight: 700 }}>{s.name}</span>
                </div>
                {s.phone && <div style={{ fontWeight: 700, color: C.amber, fontSize: 12, marginLeft: 8, flexShrink: 0 }}>{formatPhoneDisplay(s.phone)}</div>}
              </div>
            )
          })}
        </div>
      )}

      {validContacts.length > 0 && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid #F2EDE4`, paddingBottom: 4, marginBottom: 8 }}>Emergency Contacts</div>
          {validContacts.map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4` }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 12 }}>{c.name}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{c.relationship}</div>
              </div>
              {c.phone && <div style={{ fontWeight: 700, color: C.amber, fontSize: 12, marginLeft: 8 }}>{formatPhoneDisplay(c.phone)}</div>}
            </div>
          ))}
        </div>
      )}

      {hasShutoffs && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid #F2EDE4`, paddingBottom: 4, marginBottom: 8 }}>Utility Shutoffs</div>
          {shutoffs.water && <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4`, fontSize: 12 }}><span>Water shutoff</span><span style={{ color: C.brown, fontWeight: 600 }}>{shutoffs.water}</span></div>}
          {shutoffs.gas && <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4`, fontSize: 12 }}><span>Gas shutoff</span><span style={{ color: C.brown, fontWeight: 600 }}>{shutoffs.gas}</span></div>}
          {shutoffs.electric && <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4`, fontSize: 12 }}><span>Electrical panel</span><span style={{ color: C.brown, fontWeight: 600 }}>{shutoffs.electric}</span></div>}
          {shutoffs.sewer && <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4`, fontSize: 12 }}><span>Sewer cleanout</span><span style={{ color: C.brown, fontWeight: 600 }}>{shutoffs.sewer}</span></div>}
        </div>
      )}

      {hasLockbox && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid #F2EDE4`, paddingBottom: 4, marginBottom: 6 }}>Property Access</div>
          <div style={{ fontSize: 10, color: C.muted, fontStyle: 'italic', marginBottom: 8 }}>Sensitive — keep this sheet secure</div>
          {shutoffs.lockbox_location && <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4`, fontSize: 12 }}><span>Lockbox Location</span><span style={{ color: C.brown, fontWeight: 600 }}>{shutoffs.lockbox_location}</span></div>}
          {shutoffs.lockbox_code && <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 4, borderBottom: `1px solid #F2EDE4`, fontSize: 12 }}><span>Lockbox Code</span><span style={{ color: C.brown, fontWeight: 600 }}>{shutoffs.lockbox_code}</span></div>}
        </div>
      )}

      {shutoffs.notes && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid #F2EDE4`, paddingBottom: 4, marginBottom: 8 }}>Notes for First Responders</div>
          <div style={{ fontSize: 12, color: C.brown, lineHeight: 1.5 }}>{shutoffs.notes}</div>
        </div>
      )}

      {/* Footer */}
      <div style={{ background: '#F2EDE4', padding: '8px 16px', fontSize: 10, color: C.muted, textAlign: 'center' }}>
        Generated by Casavio · Print and keep visible in your home
      </div>
    </div>
  )
}

// ── MAIN WIZARD ──────────────────────────────────────────────────────────────

export default function EmergencySheetWizard({ session, homeId, userProfile, homeAddress, onComplete, onClose }) {
  const isNarrow = window.innerWidth < 720

  const [step, setStep] = useState('A')
  const [showMobilePreview, setShowMobilePreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pdfHtml, setPdfHtml] = useState(null)
  const [isDone, setIsDone] = useState(false)
  const [alert, setAlert] = useState(null)

  // Tour signal: the sheet wizard closed (welcome tour step 6 waits on it).
  useEffect(() => () => { emitTourEvent(TOUR_EVENT_TYPES.EMERGENCY_SHEET_FLOW_CLOSED, {}) }, [])

  // Step A — household members. Starts empty; useEffect below loads existing
  // rows from the database, or seeds a default "self" row for new users.
  const [members, setMembers] = useState([])
  const [membersLoaded, setMembersLoaded] = useState(false)

  // Step B — personal emergency contacts. Starts with preset slots; useEffect
  // below merges any existing contacts from the database into matching slots.
  const [contacts, setContacts] = useState(
    PRESET_CONTACTS.map(rel => ({ relationship: rel, name: '', phone: '' }))
  )
  const [contactsLoaded, setContactsLoaded] = useState(false)

  // Step C — local emergency services
  const [localServices, setLocalServices] = useState([])
  const [localServicesLoaded, setLocalServicesLoaded] = useState(false)
  const [refreshingLocal, setRefreshingLocal] = useState(false)
  const [refreshToast, setRefreshToast] = useState(null)
  const [confirmingRemove, setConfirmingRemove] = useState(null)
  const [editingService, setEditingService] = useState(null)
  const [editForm, setEditForm] = useState({ name: '', phone: '' })

  // Step D — shutoff locations
  const [shutoffs, setShutoffs] = useState({
    water: '', gas: '', electric: '', sewer: '',
    lockbox_code: '', lockbox_location: '', notes: '',
  })

  // Load existing local services on mount so returning users see their data.
  // Always prepend DEFAULT_LOCAL_SERVICES (988, Poison Control) if not already present.
  useEffect(() => {
    if (!homeId) return
    supabase.from('emergency_contacts')
      .select('*')
      .eq('home_id', homeId)
      .eq('is_local_service', true)
      .order('priority')
      .then(({ data }) => {
        const existing = data || []
        const existingTypes = new Set(existing.map(s => s.service_type).filter(Boolean))
        const missingDefaults = DEFAULT_LOCAL_SERVICES.filter(
          d => !existingTypes.has(d.service_type)
        )
        setLocalServices([...missingDefaults, ...existing])
        setLocalServicesLoaded(true)
      })
  }, [homeId])
  // Load existing household members. If none exist, seed with a single "self"
  // row using the profile name (returning users see their existing list).
  useEffect(() => {
    if (!homeId) return
    supabase.from('household_members')
      .select('*')
      .eq('home_id', homeId)
      .order('sort_order')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setMembers(data.map(m => {
            let first_name = m.first_name || ''
            let last_name = m.last_name || ''
            if (!first_name && !last_name && m.name && !m.is_pet) {
              const spaceIdx = m.name.indexOf(' ')
              if (spaceIdx > 0) {
                first_name = m.name.slice(0, spaceIdx)
                last_name = m.name.slice(spaceIdx + 1)
              } else {
                first_name = m.name
              }
            }
            return {
              name: m.name || '',
              first_name,
              last_name,
              email: m.email || '',
              phone: m.phone ? formatPhoneInput(m.phone) : '',
              relationship: (m.relationship || 'other').toLowerCase().trim(),
              notes: m.notes || '',
              is_pet: !!m.is_pet,
              pet_type: m.pet_type || '',
              non_english_languages: m.non_english_languages || '',
              preferred_hospital: m.preferred_hospital || '',
              preferred_hospital_phone: m.preferred_hospital_phone || '',
              preferred_hospital_notes: m.preferred_hospital_notes || '',
              expanded: false,
            }
          }))
        } else {
          const selfFirst = userProfile?.first_name || ''
          const selfLast = userProfile?.last_name || ''
          const selfPhone = userProfile?.phone ? formatPhoneInput(userProfile.phone) : ''
          const selfEmail = userProfile?.email || session?.user?.email || ''
          setMembers([{
            name: `${selfFirst} ${selfLast}`.trim(),
            first_name: selfFirst,
            last_name: selfLast,
            email: selfEmail,
            phone: selfPhone,
            relationship: 'self', notes: '', is_pet: false,
            pet_type: '', non_english_languages: '', preferred_hospital: '',
            preferred_hospital_phone: '', preferred_hospital_notes: '', expanded: false,
          }])
        }
        setMembersLoaded(true)
      })
  }, [homeId, userProfile])
  // Load existing personal contacts. All rows per preset category are loaded
  // (multiple family contacts, etc.). Extras appended after presets.
  useEffect(() => {
    if (!homeId) return
    supabase.from('emergency_contacts')
      .select('*')
      .eq('home_id', homeId)
      .eq('is_local_service', false)
      .order('priority')
      .then(({ data }) => {
        if (!data || data.length === 0) {
          setContactsLoaded(true)
          return
        }
        // Group ALL rows by preset category (multi-value).
        // Normalize on read: lowercase + migrate legacy label rename.
        const byRel = {}
        const extras = []
        data.forEach(c => {
          let key = (c.relationship || '').toLowerCase().trim()
          if (key === 'emergency family contact') key = 'family contact'
          if (key && PRESET_CONTACTS.some(p => p.toLowerCase() === key)) {
            if (!byRel[key]) byRel[key] = []
            byRel[key].push(c)
          } else {
            extras.push({ ...c, _normalizedRel: key })
          }
        })
        // For each preset, include every saved entry (at least one empty slot)
        const filled = PRESET_CONTACTS.flatMap(rel => {
          const matches = byRel[rel.toLowerCase()] || []
          if (matches.length === 0) return [{ relationship: rel, name: '', phone: '' }]
          return matches.map(c => ({ relationship: rel, name: c.name || '', phone: c.phone || '' }))
        })
        const extraSlots = extras.map(c => ({
          relationship: c._normalizedRel || c.relationship || '',
          name: c.name || '',
          phone: c.phone || '',
        }))
        setContacts([...filled, ...extraSlots])
        setContactsLoaded(true)
      })
  }, [homeId])
  // Load existing shutoff locations from the homes row.
  useEffect(() => {
    if (!homeId) return
    supabase.from('homes')
      .select('water_shutoff_location, gas_shutoff_location, electrical_panel_location, sewer_cleanout_location, lockbox_code, lockbox_location, emergency_notes')
      .eq('id', homeId)
      .single()
      .then(({ data }) => {
        if (!data) return
        setShutoffs({
          water: data.water_shutoff_location || '',
          gas: data.gas_shutoff_location || '',
          electric: data.electrical_panel_location || '',
          sewer: data.sewer_cleanout_location || '',
          lockbox_code: data.lockbox_code || '',
          lockbox_location: data.lockbox_location || '',
          notes: data.emergency_notes || '',
        })
      })
  }, [homeId])

  // ── MEMBERS ──
  const updateMember = (i, field, val) => {
    setMembers(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: val } : m))
  }
  const addMember = () => setMembers(prev => [...prev, { name: '', first_name: '', last_name: '', email: '', phone: '', relationship: 'other', notes: '', is_pet: false, pet_type: '', non_english_languages: '', preferred_hospital: '', preferred_hospital_phone: '', preferred_hospital_notes: '', expanded: false }])
  const removeMember = (i) => setMembers(prev => prev.filter((_, idx) => idx !== i))

  // ── CONTACTS ──
  const updateContact = (i, field, val) => {
    setContacts(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: val } : c))
  }
  const addContact = () => setContacts(prev => [...prev, { relationship: '', name: '', phone: '' }])
  const removeContact = (i) => setContacts(prev => prev.filter((_, idx) => idx !== i))
  const addContactForCategory = (rel) => setContacts(prev => {
    const lastIdx = prev.reduce((acc, c, i) => c.relationship === rel ? i : acc, -1)
    const copy = [...prev]
    copy.splice(lastIdx + 1, 0, { relationship: rel, name: '', phone: '' })
    return copy
  })

  // ── LOCAL SERVICES ──
  const updateLocalService = (i, field, val) => {
    setLocalServices(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s))
  }
  const addLocalService = () => {
    setLocalServices(prev => [...prev, { name: '', phone: '', service_type: '' }])
    setConfirmingRemove(null)
  }
  const removeLocalService = (i) => {
    setLocalServices(prev => prev.filter((_, idx) => idx !== i))
    setConfirmingRemove(null)
  }

  const refreshFromAddress = async () => {
    setRefreshingLocal(true)
    setRefreshToast(null)
    try {
      const { data, error } = await supabase.functions.invoke('lookup-emergency-services', {
        body: { home_id: homeId },
      })
      if (error || !data?.services) {
        setRefreshToast({ type: 'error', msg: 'Could not look up services for this address. Try editing manually.' })
        return
      }
      setLocalServices(data.services)
      setRefreshToast({ type: 'success', msg: `Updated — ${data.saved} service${data.saved !== 1 ? 's' : ''} found.` })
      setTimeout(() => setRefreshToast(null), 4000)
    } catch {
      setRefreshToast({ type: 'error', msg: 'Could not look up services. Try again later.' })
    } finally {
      setRefreshingLocal(false)
    }
  }

  // ── SHUTOFFS ──
  const setShutoff = (field) => (e) => setShutoffs(prev => ({ ...prev, [field]: e.target.value }))

  // ── SAVE HELPERS ──
  // Idempotent save: delete all existing rows for this home, then re-insert
  // current state. Same pattern as saveLocalServices. Prevents the duplicate-
  // row bug where every wizard pass appended new copies.
  const saveMembers = async () => {
    const { data: existing } = await supabase
      .from('household_members')
      .select('name, linked_user_id, invite_status, invite_sent_at, invited_at_email')
      .eq('home_id', homeId)
    const preserved = {}
    if (existing) {
      for (const row of existing) {
        if (row.name) preserved[row.name.trim().toLowerCase()] = row
      }
    }

    await supabase.from('household_members').delete().eq('home_id', homeId)
    const valid = members.filter(m =>
      m.is_pet ? m.name.trim() : (m.first_name.trim() || m.last_name.trim())
    )
    if (!valid.length) return
    const inserts = valid.map((m, i) => {
      const firstName = m.first_name.trim()
      const lastName = m.last_name.trim()
      const phoneDigits = m.phone ? m.phone.replace(/\D/g, '') : null
      const computedName = m.is_pet ? m.name.trim() : `${firstName} ${lastName}`.trim()
      const preservedRow = preserved[computedName.toLowerCase()] || {}
      return {
        home_id: homeId,
        name: computedName,
        first_name: m.is_pet ? null : (firstName || null),
        last_name: m.is_pet ? null : (lastName || null),
        email: (!m.is_pet && m.email?.trim()) ? m.email.trim() : null,
        phone: (!m.is_pet && phoneDigits) ? phoneDigits : null,
        relationship: m.is_pet ? null : (m.relationship || 'other').toLowerCase().trim(),
        is_pet: m.is_pet,
        pet_type: m.is_pet ? (m.pet_type || 'Pet') : null,
        notes: m.notes || null,
        sort_order: i,
        non_english_languages: (!m.is_pet && m.non_english_languages) ? m.non_english_languages.trim() : null,
        preferred_hospital: (!m.is_pet && m.preferred_hospital) ? m.preferred_hospital.trim() : null,
        preferred_hospital_phone: (!m.is_pet && m.preferred_hospital_phone) ? m.preferred_hospital_phone.trim() : null,
        preferred_hospital_notes: (!m.is_pet && m.preferred_hospital_notes) ? m.preferred_hospital_notes.trim() : null,
        linked_user_id: preservedRow.linked_user_id || null,
        invite_status: preservedRow.invite_status || null,
        invite_sent_at: preservedRow.invite_sent_at || null,
        invited_at_email: preservedRow.invited_at_email || null,
      }
    })
    await supabase.from('household_members').insert(inserts)
  }

  // Idempotent save: delete all non-local-service contacts for this home,
  // then re-insert current state. Local services use a separate save function.
  const saveContacts = async () => {
    await supabase.from('emergency_contacts')
      .delete()
      .eq('home_id', homeId)
      .eq('is_local_service', false)
    const valid = contacts.filter(c => c.name.trim() && c.phone.trim())
    if (!valid.length) return
    const inserts = valid.map((c, i) => ({
      home_id: homeId,
      user_id: session.user.id,
      name: c.name.trim(),
      relationship: c.relationship ? c.relationship.toLowerCase().trim() : null,
      phone: c.phone.replace(/\D/g, ''),
      is_local_service: false,
      priority: i + 1,
    }))
    await supabase.from('emergency_contacts').insert(inserts)
  }

  const saveLocalServices = async () => {
    // Delete all existing local-service rows then re-insert — same idempotent pattern as the edge function
    await supabase.from('emergency_contacts')
      .delete()
      .eq('home_id', homeId)
      .eq('is_local_service', true)
    const valid = localServices.filter(s => s.name?.trim())
    if (!valid.length) return
    await supabase.from('emergency_contacts').insert(
      valid.map((s, i) => ({
        home_id: homeId,
        name: s.name.trim(),
        phone: s.phone || null,
        notes: s.notes || null,
        service_type: s.service_type || null,
        relationship: s.service_type || s.relationship || null,
        is_local_service: true,
        priority: i,
      }))
    )
  }

  const saveShutoffs = async () => {
    await supabase.from('homes').update({
      water_shutoff_location:    shutoffs.water   || null,
      gas_shutoff_location:      shutoffs.gas     || null,
      electrical_panel_location: shutoffs.electric || null,
      sewer_cleanout_location:   shutoffs.sewer   || null,
      lockbox_code:              shutoffs.lockbox_code   || null,
      lockbox_location:          shutoffs.lockbox_location || null,
      emergency_notes:           shutoffs.notes   || null,
    }).eq('id', homeId)
  }

  const generatePdf = async () => {
    const { data, error } = await supabase.functions.invoke('generate-emergency-pdf', {
      body: { home_id: homeId },
    })
    if (error || !data?.html) {
      setAlert('Could not generate the PDF. Your data is saved — try again from the Emergency Sheet section.')
      return false
    }
    setPdfHtml(data.html)
    // Tour signal: the sheet was actually generated (welcome tour step 6 waits on this).
    emitTourEvent(TOUR_EVENT_TYPES.EMERGENCY_SHEET_GENERATED, { home_id: homeId })
    return true
  }

  // ── NAVIGATION ──
  // NOTE: onComplete() is intentionally NOT called when we reach the celebration
  // screen — calling it triggers the parent's handleEmergencySetupComplete which
  // unmounts this wizard, preventing the celebration from rendering. Instead
  // onComplete fires from the celebration's "Done" button (see openPdf area).
  const goNext = async () => {
    setAlert(null)
    if (step === 'A') {
      const err = validateStepA(members)
      if (err) { setAlert(err); return }
    }
    setSaving(true)
    try {
      if (step === 'A') { await saveMembers(); setStep('B') }
      else if (step === 'B') { await saveContacts(); setStep('C') }
      else if (step === 'C') { await saveLocalServices(); setStep('D') }
      else if (step === 'D') { await saveShutoffs(); setStep('E') }
      else if (step === 'E') {
        const ok = await generatePdf()
        if (ok) setIsDone(true)  // celebration screen handles onComplete on Done
      }
    } catch (err) {
      setAlert('Something went wrong. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const skip = async () => {
    setSaving(true)
    try {
      if (step === 'A') setStep('B')
      else if (step === 'B') setStep('C')
      else if (step === 'C') setStep('D')
      else if (step === 'D') { await saveShutoffs(); setStep('E') }
    } finally {
      setSaving(false)
    }
  }

  const goBack = () => {
    if (step === 'B') setStep('A')
    else if (step === 'C') setStep('B')
    else if (step === 'D') setStep('C')
    else if (step === 'E') setStep('D')
  }

  const openPdf = () => {
    if (!pdfHtml) return
    const win = window.open('', '_blank')
    if (win) {
      win.document.write(pdfHtml)
      win.document.close()
      setTimeout(() => win.print(), 500)
    }
  }

  // ── LABELS ──
  const STEP_LABELS = {
    A: 'Who lives here',
    B: 'Key contacts',
    C: 'Local Emergency Services',
    D: 'Where things are',
    E: 'Review & print',
  }
  const steps = ['A', 'B', 'C', 'D', 'E']
  const stepIdx = steps.indexOf(step)

  // ── INPUT STYLES ──
  const inputStyle = {
    width: '100%', padding: '10px 12px',
    border: `1px solid ${C.border}`, borderRadius: 8,
    fontSize: 14, fontFamily: cal, color: C.brown,
    background: C.card, outline: 'none', boxSizing: 'border-box',
    WebkitAppearance: 'none',
  }
  const labelStyle = { fontSize: 11, color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: cal, display: 'block', marginBottom: 5 }
  const fieldWrap = { marginBottom: 14 }

  // ── CELEBRATION ──
  if (isDone && pdfHtml) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.6)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: C.card, borderRadius: 20, padding: '40px 32px', maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏠</div>
          <h2 style={{ fontFamily: geo, fontSize: 24, color: C.brown, marginBottom: 12, fontWeight: 700 }}>Your emergency sheet is ready.</h2>
          <p style={{ fontSize: 14, color: C.muted, fontFamily: cal, lineHeight: 1.7, marginBottom: 24 }}>
            We'll keep it current automatically — anytime you update your household info, the sheet updates too. Print a fresh copy any time from the Emergency Sheet section in your home profile.
          </p>
          <div style={{ background: '#FFF8E7', border: `1px solid ${C.amber}44`, borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: C.brown, fontFamily: cal, lineHeight: 1.5 }}>
            💡 Most people stick this on the fridge with a magnet, or post it inside the door of the kitchen utility closet.
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={openPdf}
              style={{ background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: geo }}
            >
              🖨️ Open &amp; Print
            </button>
            <button
              onClick={() => { onComplete(); onClose() }}
              style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 24px', fontSize: 14, color: C.muted, cursor: 'pointer', fontFamily: cal }}
            >
              Done
            </button>
          </div>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: cal, marginTop: 16 }}>
            In the print dialog, choose "Save as PDF" to download a copy.
          </div>
        </div>
      </div>
    )
  }

  // ── MAIN WIZARD LAYOUT ──
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.6)', zIndex: 1500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{
        background: C.bg, width: '100%', maxWidth: 960,
        maxHeight: '94vh', borderRadius: '20px 20px 0 0',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* ── HEADER ── */}
        <div style={{ background: C.card, borderBottom: `1px solid ${C.border}`, padding: '16px 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: C.amber, fontFamily: cal, letterSpacing: '2px', textTransform: 'uppercase' }}>Emergency Sheet Setup</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: geo, color: C.brown, marginTop: 2 }}>
                {STEP_LABELS[step]}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isNarrow && step !== 'E' && (
                <button
                  onClick={() => setShowMobilePreview(v => !v)}
                  style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 12, color: C.amber, cursor: 'pointer', fontFamily: cal }}
                >
                  {showMobilePreview ? 'Hide preview' : 'Preview sheet'}
                </button>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: C.muted, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>✕</button>
            </div>
          </div>
          {/* Step dots */}
          <div style={{ display: 'flex', gap: 6 }}>
            {steps.map((s, i) => (
              <div key={s} style={{
                height: 4, flex: 1, borderRadius: 2,
                background: i < stepIdx ? C.amber : i === stepIdx ? C.amber : C.border,
                opacity: i < stepIdx ? 0.5 : 1,
                transition: 'background 0.2s',
              }} />
            ))}
          </div>
        </div>

        {/* ── BODY ── */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

          {/* Form pane */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '20px 24px',
            display: (!isNarrow || !showMobilePreview) ? 'block' : 'none',
          }}>

            {alert && (
              <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}33`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.danger, fontFamily: cal, marginBottom: 16 }}>
                {alert}
              </div>
            )}

            {/* ── STEP A — WHO LIVES HERE ── */}
            {step === 'A' && (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
                  <p style={{ fontSize: 14, color: C.muted, fontFamily: cal, lineHeight: 1.6, margin: 0, flex: 1 }}>
                    Add everyone who lives in your home — family, roommates, and pets. First responders use this list to know how many people and animals to look for.
                  </p>
                  <button
                    onClick={goNext}
                    disabled={saving}
                    style={{ flexShrink: 0, background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.amber, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}
                  >
                    Next →
                  </button>
                </div>
                {members.map((m, i) => {
                  const isSelf = m.relationship === 'self'
                  return (
                    <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px', marginBottom: 12, position: 'relative' }}>
                      {members.length > 1 && !isSelf && (
                        <button onClick={() => removeMember(i)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', fontSize: 18, color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
                      )}

                      {isSelf ? (
                        /* ── SELF ROW — read-only contact fields ── */
                        <>
                          <div style={{ fontSize: 11, color: C.amber, fontFamily: cal, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 10 }}>You</div>
                          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 10 }}>
                            <div>
                              <label style={labelStyle}>First name</label>
                              <input style={{ ...inputStyle, background: '#F5F3F0', color: C.muted }} value={m.first_name} readOnly />
                            </div>
                            <div>
                              <label style={labelStyle}>Last name</label>
                              <input style={{ ...inputStyle, background: '#F5F3F0', color: C.muted }} value={m.last_name} readOnly />
                            </div>
                            <div>
                              <label style={labelStyle}>Email</label>
                              <input style={{ ...inputStyle, background: '#F5F3F0', color: C.muted }} value={m.email} readOnly />
                            </div>
                            <div>
                              <label style={labelStyle}>Phone</label>
                              <input style={{ ...inputStyle, background: '#F5F3F0', color: C.muted }} value={m.phone} readOnly />
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: C.muted, fontFamily: cal, fontStyle: 'italic', marginBottom: 12 }}>
                            Edit your contact info in My Account → Profile
                          </div>
                          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                            <button
                              onClick={() => updateMember(i, 'expanded', !m.expanded)}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: C.amber, fontFamily: cal, display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              {m.expanded ? '▾ Hide medical & language info' : '+ Add medical & language info (optional)'}
                            </button>
                            {m.expanded && (
                              <div style={{ marginTop: 12 }}>
                                <div style={fieldWrap}>
                                  <label style={labelStyle}>Languages other than English</label>
                                  <input style={inputStyle} placeholder="e.g., Spanish, Mandarin" value={m.non_english_languages} onChange={e => updateMember(i, 'non_english_languages', e.target.value)} />
                                </div>
                                <div style={fieldWrap}>
                                  <label style={labelStyle}>Preferred hospital</label>
                                  <input style={inputStyle} placeholder="e.g., Marin General Hospital" value={m.preferred_hospital} onChange={e => updateMember(i, 'preferred_hospital', e.target.value)} />
                                </div>
                                <div style={fieldWrap}>
                                  <label style={labelStyle}>Hospital phone</label>
                                  <input style={inputStyle} placeholder="(415) 555-0000" value={m.preferred_hospital_phone} onChange={e => updateMember(i, 'preferred_hospital_phone', e.target.value)} inputMode="tel" />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <label style={labelStyle}>Notes for first responders</label>
                                  <textarea
                                    style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }}
                                    placeholder="e.g., Allergic to penicillin. Takes blood thinners. Pacemaker."
                                    value={m.preferred_hospital_notes}
                                    onChange={e => updateMember(i, 'preferred_hospital_notes', e.target.value)}
                                    rows={2}
                                  />
                                </div>
                                <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', fontFamily: cal, lineHeight: 1.5 }}>
                                  This info appears on your printed Emergency Sheet to help first responders. Only fill in what you're comfortable sharing.
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : m.is_pet ? (
                        /* ── PET ROW ── */
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 10 }}>
                            <div>
                              <label style={labelStyle}>Pet name</label>
                              <input style={inputStyle} placeholder="e.g., Buddy" value={m.name} onChange={e => updateMember(i, 'name', e.target.value)} />
                            </div>
                            <div>
                              <label style={labelStyle}>Pet type</label>
                              <select style={inputStyle} value={m.pet_type} onChange={e => updateMember(i, 'pet_type', e.target.value)}>
                                <option value="">Select type</option>
                                {PET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontFamily: cal, color: C.brown, cursor: 'pointer', userSelect: 'none' }}>
                            <input type="checkbox" checked={m.is_pet} onChange={e => updateMember(i, 'is_pet', e.target.checked)} style={{ width: 16, height: 16, accentColor: C.amber }} />
                            This is a pet
                          </label>
                        </>
                      ) : (
                        /* ── NON-SELF, NON-PET ROW ── */
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 10 }}>
                            <div>
                              <label style={labelStyle}>First name</label>
                              <input style={inputStyle} placeholder="First name" value={m.first_name} onChange={e => updateMember(i, 'first_name', e.target.value)} />
                            </div>
                            <div>
                              <label style={labelStyle}>Last name</label>
                              <input style={inputStyle} placeholder="Last name" value={m.last_name} onChange={e => updateMember(i, 'last_name', e.target.value)} />
                            </div>
                            <div>
                              <label style={labelStyle}>
                                Email <span style={{ fontStyle: 'italic', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>(optional — for inviting to Casavio later)</span>
                              </label>
                              <input style={inputStyle} type="email" placeholder="email@example.com" value={m.email} onChange={e => updateMember(i, 'email', e.target.value)} inputMode="email" />
                            </div>
                            <div>
                              <label style={labelStyle}>Phone <span style={{ fontStyle: 'italic', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>(optional)</span></label>
                              <input style={inputStyle} type="tel" placeholder="(555) 555-1234" value={m.phone}
                                onChange={e => updateMember(i, 'phone', formatPhoneInput(e.target.value))} inputMode="tel" />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label style={labelStyle}>Relationship</label>
                            <select style={{ ...inputStyle, width: isNarrow ? '100%' : '50%' }} value={m.relationship} onChange={e => updateMember(i, 'relationship', e.target.value)}>
                              {RELATIONSHIPS.filter(r => r !== 'pet').map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontFamily: cal, color: C.brown, cursor: 'pointer', userSelect: 'none' }}>
                            <input type="checkbox" checked={m.is_pet} onChange={e => updateMember(i, 'is_pet', e.target.checked)} style={{ width: 16, height: 16, accentColor: C.amber }} />
                            This is a pet
                          </label>
                          <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                            <button
                              onClick={() => updateMember(i, 'expanded', !m.expanded)}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: C.amber, fontFamily: cal, display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              {m.expanded ? '▾ Hide medical & language info' : '+ Add medical & language info (optional)'}
                            </button>
                            {m.expanded && (
                              <div style={{ marginTop: 12 }}>
                                <div style={fieldWrap}>
                                  <label style={labelStyle}>Languages other than English</label>
                                  <input style={inputStyle} placeholder="e.g., Spanish, Mandarin" value={m.non_english_languages} onChange={e => updateMember(i, 'non_english_languages', e.target.value)} />
                                </div>
                                <div style={fieldWrap}>
                                  <label style={labelStyle}>Preferred hospital</label>
                                  <input style={inputStyle} placeholder="e.g., Marin General Hospital" value={m.preferred_hospital} onChange={e => updateMember(i, 'preferred_hospital', e.target.value)} />
                                </div>
                                <div style={fieldWrap}>
                                  <label style={labelStyle}>Hospital phone</label>
                                  <input style={inputStyle} placeholder="(415) 555-0000" value={m.preferred_hospital_phone} onChange={e => updateMember(i, 'preferred_hospital_phone', e.target.value)} inputMode="tel" />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <label style={labelStyle}>Notes for first responders</label>
                                  <textarea
                                    style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }}
                                    placeholder="e.g., Allergic to penicillin. Takes blood thinners. Pacemaker."
                                    value={m.preferred_hospital_notes}
                                    onChange={e => updateMember(i, 'preferred_hospital_notes', e.target.value)}
                                    rows={2}
                                  />
                                </div>
                                <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', fontFamily: cal, lineHeight: 1.5 }}>
                                  This info appears on your printed Emergency Sheet to help first responders. Only fill in what you're comfortable sharing.
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
                <button
                  onClick={addMember}
                  style={{ background: 'none', border: `1px dashed ${C.amber}`, borderRadius: 10, padding: '10px 16px', fontSize: 13, color: C.amber, cursor: 'pointer', fontFamily: cal, width: '100%', marginBottom: 4 }}
                >
                  + Add another person or pet
                </button>
              </>
            )}

            {/* ── STEP B — KEY CONTACTS ── */}
            {step === 'B' && (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
                  <p style={{ fontSize: 14, color: C.muted, fontFamily: cal, lineHeight: 1.6, margin: 0, flex: 1 }}>
                    Add the people who should be called in an emergency. Leave any blank that don't apply — only filled contacts appear on the sheet.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={goBack} disabled={saving} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.muted, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>← Back</button>
                    <button onClick={goNext} disabled={saving} style={{ flexShrink: 0, background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.amber, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>Next →</button>
                  </div>
                </div>

                {/* Preset categories — each supports multiple entries */}
                {PRESET_CONTACTS.map(rel => {
                  const entries = contacts.reduce((acc, c, i) => {
                    if (c.relationship === rel) acc.push(i)
                    return acc
                  }, [])
                  return (
                    <div key={rel} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 11, color: C.amber, fontFamily: cal, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>
                        {rel}
                      </div>
                      {entries.map(i => {
                        const c = contacts[i]
                        return (
                          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 8, position: 'relative' }}>
                            {entries.length > 1 && (
                              <button onClick={() => removeContact(i)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', fontSize: 18, color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12 }}>
                              <div>
                                <label style={labelStyle}>Name</label>
                                <input style={inputStyle} placeholder="Full name" value={c.name} onChange={e => updateContact(i, 'name', e.target.value)} />
                              </div>
                              <div>
                                <label style={labelStyle}>Phone</label>
                                <input style={inputStyle} placeholder="(555) 555-1234" value={c.phone} onChange={e => updateContact(i, 'phone', e.target.value)} inputMode="tel" />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                      <button
                        onClick={() => addContactForCategory(rel)}
                        style={{ background: 'none', border: 'none', padding: '2px 0', fontSize: 12, color: C.amber, cursor: 'pointer', fontFamily: cal }}
                      >
                        + Add another {rel.toLowerCase()}
                      </button>
                    </div>
                  )
                })}

                {/* Custom (non-preset) extras */}
                {contacts.map((c, i) => {
                  if (PRESET_CONTACTS.includes(c.relationship)) return null
                  return (
                    <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, position: 'relative' }}>
                      <button onClick={() => removeContact(i)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', fontSize: 18, color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
                      <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Name</label>
                          <input style={inputStyle} placeholder="Full name" value={c.name} onChange={e => updateContact(i, 'name', e.target.value)} />
                        </div>
                        <div>
                          <label style={labelStyle}>Phone</label>
                          <input style={inputStyle} placeholder="(555) 555-1234" value={c.phone} onChange={e => updateContact(i, 'phone', e.target.value)} inputMode="tel" />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={labelStyle}>Relationship / Category</label>
                          <input style={inputStyle} placeholder="e.g. Plumber, Neighbor, Aunt" value={c.relationship} onChange={e => updateContact(i, 'relationship', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )
                })}

                <button
                  onClick={addContact}
                  style={{ background: 'none', border: `1px dashed ${C.amber}`, borderRadius: 10, padding: '10px 16px', fontSize: 13, color: C.amber, cursor: 'pointer', fontFamily: cal, width: '100%', marginBottom: 4 }}
                >
                  + Add custom contact
                </button>
              </>
            )}

            {/* ── STEP C — LOCAL EMERGENCY SERVICES ── */}
            {step === 'C' && (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                  <p style={{ fontSize: 14, color: C.muted, fontFamily: cal, lineHeight: 1.6, margin: 0, flex: 1, fontStyle: 'italic' }}>
                    These are auto-populated based on your home's address. Tap any to edit if a phone number is wrong.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={goBack} disabled={saving} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.muted, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>← Back</button>
                    <button onClick={goNext} disabled={saving} style={{ flexShrink: 0, background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.amber, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>Next →</button>
                  </div>
                </div>

                {/* Refresh button */}
                <button
                  onClick={refreshFromAddress}
                  disabled={refreshingLocal}
                  style={{
                    background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8,
                    padding: '8px 16px', fontSize: 13, color: C.amber, cursor: refreshingLocal ? 'default' : 'pointer',
                    fontFamily: cal, marginBottom: 14, opacity: refreshingLocal ? 0.6 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {refreshingLocal ? 'Looking up…' : '🔄 Refresh from address'}
                </button>

                {/* Toast */}
                {refreshToast && (
                  <div style={{
                    background: refreshToast.type === 'success' ? C.successBg : C.dangerBg,
                    border: `1px solid ${refreshToast.type === 'success' ? C.success : C.danger}33`,
                    borderRadius: 8, padding: '9px 14px', fontSize: 13,
                    color: refreshToast.type === 'success' ? C.success : C.danger,
                    fontFamily: cal, marginBottom: 14,
                  }}>
                    {refreshToast.msg}
                  </div>
                )}

                {/* Empty state */}
                {localServicesLoaded && localServices.length === 0 && !refreshingLocal && (
                  <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: '20px 16px', textAlign: 'center', color: C.muted, fontSize: 13, fontFamily: cal, marginBottom: 12 }}>
                    No local services yet — click "Refresh from address" to auto-populate, or add them manually below.
                  </div>
                )}

                {/* Service cards — read-only by default, expand to edit on pencil tap */}
                {localServices.map((s, i) => {
                  const cfg = SERVICE_TYPE_DISPLAY[s.service_type] || SERVICE_TYPE_DISPLAY.other
                  const isEditing = editingService === i
                  return (
                    <div key={i} style={{ background: C.card, border: `1px solid ${isEditing ? C.amber : C.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
                      {isEditing ? (
                        // ── EDIT MODE ──
                        <>
                          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: cal, marginBottom: 10 }}>
                            <span style={{ marginRight: 4 }}>{cfg.icon}</span>
                            {cfg.label}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                              <label style={labelStyle}>Service name</label>
                              <input
                                style={inputStyle}
                                placeholder="e.g., Marin County Sheriff"
                                value={editForm.name}
                                onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                autoFocus
                              />
                            </div>
                            <div>
                              <label style={labelStyle}>Phone</label>
                              <input
                                style={inputStyle}
                                placeholder="(555) 555-1234"
                                value={editForm.phone}
                                onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                inputMode="tel"
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => { setEditingService(null) }}
                              style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 7, padding: '7px 14px', fontSize: 13, color: C.muted, cursor: 'pointer', fontFamily: cal }}
                            >Cancel</button>
                            <button
                              onClick={() => {
                                updateLocalService(i, 'name', editForm.name)
                                updateLocalService(i, 'phone', editForm.phone)
                                setEditingService(null)
                              }}
                              style={{ background: C.amber, border: 'none', borderRadius: 7, padding: '7px 18px', fontSize: 13, color: '#fff', cursor: 'pointer', fontFamily: cal, fontWeight: 700 }}
                            >Save</button>
                          </div>
                        </>
                      ) : (
                        // ── READ-ONLY MODE ──
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: cal, marginBottom: 4 }}>
                              <span style={{ marginRight: 4 }}>{cfg.icon}</span>
                              {cfg.label}
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.brown, fontFamily: geo }}>{s.name || '(no name)'}</div>
                            {s.phone && <div style={{ fontSize: 13, color: C.amber, fontFamily: cal, marginTop: 2 }}>{formatPhoneDisplay(s.phone)}</div>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <button
                              onClick={() => {
                                setEditForm({ name: s.name || '', phone: s.phone || '' })
                                setEditingService(i)
                                setConfirmingRemove(null)
                              }}
                              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, width: 32, height: 32, cursor: 'pointer', color: C.muted, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              title="Edit"
                            >✏</button>
                            {confirmingRemove === i ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <button
                                  onClick={() => removeLocalService(i)}
                                  style={{ background: C.danger, border: 'none', borderRadius: 4, padding: '4px 8px', fontSize: 11, color: '#fff', cursor: 'pointer', fontFamily: cal }}
                                >Yes</button>
                                <button
                                  onClick={() => setConfirmingRemove(null)}
                                  style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 11, color: C.muted, cursor: 'pointer', fontFamily: cal }}
                                >No</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmingRemove(i)}
                                style={{ background: 'none', border: `1px solid ${C.danger}44`, borderRadius: 6, width: 32, height: 32, cursor: 'pointer', color: C.danger, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Remove"
                              >×</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}

                <button
                  onClick={addLocalService}
                  style={{ background: 'none', border: `1px dashed ${C.amber}`, borderRadius: 10, padding: '10px 16px', fontSize: 13, color: C.amber, cursor: 'pointer', fontFamily: cal, width: '100%', marginBottom: 4 }}
                >
                  + Add another local service
                </button>
              </>
            )}

            {/* ── STEP D — WHERE THINGS ARE ── */}
            {step === 'D' && (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
                  <p style={{ fontSize: 14, color: C.muted, fontFamily: cal, lineHeight: 1.6, margin: 0, flex: 1 }}>
                    Where are the shutoffs? Leave blank anything you're unsure of — you can add them later. Even one location is helpful.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={goBack} disabled={saving} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.muted, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>← Back</button>
                    <button onClick={goNext} disabled={saving} style={{ flexShrink: 0, background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.amber, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>Next →</button>
                  </div>
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.brown, fontFamily: geo, marginBottom: 14 }}>Utility Shutoffs</div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Water shutoff location</label>
                    <input style={inputStyle} placeholder="e.g., garage, blue handle on back wall" value={shutoffs.water} onChange={setShutoff('water')} />
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Gas shutoff location</label>
                    <input style={inputStyle} placeholder="e.g., left side of house, yellow lever" value={shutoffs.gas} onChange={setShutoff('gas')} />
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Electrical panel location</label>
                    <input style={inputStyle} placeholder="e.g., garage, gray box on north wall" value={shutoffs.electric} onChange={setShutoff('electric')} />
                  </div>
                  <div>
                    <label style={labelStyle}>Sewer cleanout location (optional)</label>
                    <input style={inputStyle} placeholder="e.g., backyard, near fence" value={shutoffs.sewer} onChange={setShutoff('sewer')} />
                  </div>
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.brown, fontFamily: geo, marginBottom: 14 }}>Lockbox / Key Access (optional)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Lockbox code</label>
                      <input style={inputStyle} placeholder="e.g., 2847" value={shutoffs.lockbox_code} onChange={setShutoff('lockbox_code')} />
                    </div>
                    <div>
                      <label style={labelStyle}>Lockbox location</label>
                      <input style={inputStyle} placeholder="e.g., front door handle" value={shutoffs.lockbox_location} onChange={setShutoff('lockbox_location')} />
                    </div>
                  </div>
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px' }}>
                  <label style={{ ...labelStyle, marginBottom: 8 }}>Notes for first responders (optional)</label>
                  <textarea
                    style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                    placeholder="e.g., Oxygen tank in master bedroom. Dog is friendly. Back gate code: 1234."
                    value={shutoffs.notes}
                    onChange={setShutoff('notes')}
                  />
                </div>
              </>
            )}

            {/* ── STEP E — REVIEW & PRINT ── */}
            {step === 'E' && (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
                  <p style={{ fontSize: 14, color: C.muted, fontFamily: cal, lineHeight: 1.6, margin: 0, flex: 1 }}>
                    Your sheet is ready to generate. Click the button below — it takes about 5 seconds. Then open it, print it, and post it somewhere visible in your home.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={goBack} disabled={saving} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.muted, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>← Back</button>
                    <button onClick={goNext} disabled={saving} style={{ flexShrink: 0, background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, color: C.amber, cursor: saving ? 'default' : 'pointer', fontFamily: cal, opacity: saving ? 0.5 : 1, whiteSpace: 'nowrap' }}>🖨️ Generate PDF</button>
                  </div>
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px', marginBottom: 20 }}>
                  <SheetPreview members={members} contacts={contacts} localServices={localServices} shutoffs={shutoffs} homeAddress={homeAddress} />
                </div>
              </>
            )}

            {/* ── NAV BUTTONS ── */}
            <div style={{ display: 'flex', gap: 10, marginTop: 8, paddingBottom: 8 }}>
              {step !== 'A' && (
                <button onClick={goBack} disabled={saving} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 10, padding: '11px 16px', fontSize: 13, color: C.muted, cursor: 'pointer', fontFamily: cal }}>
                  ← Back
                </button>
              )}
              <div style={{ flex: 1 }} />
              {step !== 'E' && (
                <button onClick={skip} disabled={saving} style={{ background: 'none', border: 'none', fontSize: 13, color: C.muted, cursor: 'pointer', fontFamily: cal, padding: '11px 8px' }}>
                  Skip
                </button>
              )}
              <button
                onClick={goNext}
                disabled={saving}
                style={{ background: saving ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 700, color: saving ? C.muted : '#fff', cursor: saving ? 'default' : 'pointer', fontFamily: geo, transition: 'all 0.2s' }}
              >
                {saving ? '...' : step === 'E' ? '🖨️ Generate PDF' : 'Next →'}
              </button>
            </div>
          </div>

          {/* Preview pane — desktop always visible, mobile toggle */}
          {(!isNarrow || showMobilePreview) && step !== 'E' && (
            <div style={{
              width: isNarrow ? '100%' : '380px',
              flexShrink: 0,
              overflowY: 'auto',
              padding: '20px 20px 20px 4px',
              borderLeft: isNarrow ? 'none' : `1px solid ${C.border}`,
              background: isNarrow ? C.bg : 'transparent',
            }}>
              <div style={{ fontSize: 11, color: C.muted, fontFamily: cal, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 10 }}>
                Live Preview
              </div>
              <SheetPreview members={members} contacts={contacts} localServices={localServices} shutoffs={shutoffs} homeAddress={homeAddress} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
